/**
 * Contracts Tab Component for Event Details Dialog
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, Check, Clock, AlertCircle, Send, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContractsTab({ 
  booking, 
  contracts = [], 
  onContractsUpdate,
  onOpenCounterSign,
  formatDate 
}) {
  const [sendingReminder, setSendingReminder] = useState(null);

  const getStatusBadge = (contract) => {
    if (contract.status === 'fully_signed') {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Fully Signed</Badge>;
    }
    if (contract.status === 'counter_signed') {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Counter-signed</Badge>;
    }
    if (contract.status === 'client_signed') {
      return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Awaiting Counter-signature</Badge>;
    }
    if (contract.status === 'sent') {
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Sent - Awaiting Client</Badge>;
    }
    return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Draft</Badge>;
  };

  const handleSendReminder = async (contract) => {
    setSendingReminder(contract.id);
    try {
      await axios.post(`${API}/contracts/${contract.id}/send-reminder`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Reminder sent to client');
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleDownload = async (contract) => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${contract.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download contract');
    }
  };

  const pendingContracts = contracts.filter(c => c.status === 'client_signed');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-400" />
          Contracts
          {contracts.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-sm">
              {contracts.length}
            </span>
          )}
        </h3>
      </div>

      {/* Alert for pending signatures */}
      {pendingContracts.length > 0 && (
        <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
          <div className="flex items-center gap-2 text-orange-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{pendingContracts.length} contract(s) awaiting your counter-signature</span>
          </div>
        </div>
      )}

      {contracts.length > 0 ? (
        <div className="space-y-3">
          {contracts.map((contract) => (
            <div
              key={contract.id}
              className="p-4 rounded-lg bg-muted/30 border border-border"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-medium">{contract.template_name || 'Contract'}</span>
                    {getStatusBadge(contract)}
                  </div>
                  
                  {/* Signature Status */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {/* Client Signature */}
                    <div className="flex items-center gap-2">
                      {contract.client_signed_at ? (
                        <>
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">
                            Client signed {formatDate ? formatDate(contract.client_signed_at) : contract.client_signed_at}
                          </span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-orange-400" />
                          <span className="text-orange-400">Awaiting client signature</span>
                        </>
                      )}
                    </div>
                    
                    {/* Company Signature */}
                    <div className="flex items-center gap-2">
                      {contract.company_signed_at ? (
                        <>
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">
                            Counter-signed {formatDate ? formatDate(contract.company_signed_at) : contract.company_signed_at}
                          </span>
                        </>
                      ) : contract.client_signed_at ? (
                        <>
                          <Clock className="w-4 h-4 text-orange-400" />
                          <span className="text-orange-400">Needs your counter-signature</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">Pending client signature first</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {contract.status === 'client_signed' && onOpenCounterSign && (
                    <Button
                      size="sm"
                      onClick={() => onOpenCounterSign(contract)}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Counter-sign
                    </Button>
                  )}
                  
                  {contract.status === 'sent' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendReminder(contract)}
                      disabled={sendingReminder === contract.id}
                      className="border-border"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Remind
                    </Button>
                  )}
                  
                  {contract.signing_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(contract.signing_url, '_blank')}
                      className="border-border"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  )}
                  
                  {(contract.status === 'fully_signed' || contract.status === 'counter_signed') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(contract)}
                      className="border-border"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No contracts yet</p>
          <p className="text-sm">Contracts will appear here when created</p>
        </div>
      )}
    </div>
  );
}
